/**
 * Multi-page PDF export of the Readiness Workbook.
 * Text-first jsPDF renderer — sharper print output than DOM rasterisation.
 * Structure mirrors the on-screen workbook: meta, score summary, each
 * part in narrative form with the user's answers, top-5 actions,
 * one-year promise, and Future You letter.
 */
import jsPDF from "jspdf";

const TIER = (s) =>
  s >= 80 ? "Strong Foundation" :
  s >= 60 ? "Building Momentum" :
  s >= 40 ? "Important Work Ahead" :
  "You've Begun";

const TB_LABEL = {
  vision: "Retirement vision",
  budget: "Budget & cash flow",
  debt: "Debt management",
  emergency: "Emergency savings",
  retirement: "Retirement savings",
  social: "Social Security",
  healthcare: "Healthcare",
  investments: "Investments",
  taxes: "Taxes & withdrawals",
  estate: "Estate & protection",
};

const usd = (n) => `$${(Number(n) || 0).toLocaleString()}`;
const sum = (o) => Object.values(o || {}).reduce((a, v) => a + (Number(v) || 0), 0);
const has = (v) => v != null && String(v).trim() !== "";

export function exportWorkbookPDF(w) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 48;
  let y = margin;

  const line = (text, { size = 11, bold = false, color = "#1C2B23", spaceAfter = 4, italic = false } = {}) => {
    doc.setFont("helvetica", bold ? "bold" : italic ? "italic" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(color);
    const split = doc.splitTextToSize(text ?? "", pageW - margin * 2);
    ensureRoom(split.length * size * 1.2 + spaceAfter);
    doc.text(split, margin, y);
    y += split.length * size * 1.2 + spaceAfter;
  };
  const rule = () => {
    ensureRoom(20);
    doc.setDrawColor("#E5E2DC");
    doc.line(margin, y, pageW - margin, y);
    y += 14;
  };
  const ensureRoom = (needed) => {
    if (y + needed > pageH - margin) {
      doc.addPage();
      y = margin;
    }
  };
  const kv = (label, value) => {
    if (!has(value)) return;
    ensureRoom(24);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor("#5C6B62");
    doc.text(String(label).toUpperCase(), margin, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor("#1C2B23");
    const split = doc.splitTextToSize(String(value), pageW - margin * 2);
    doc.text(split, margin, y + 13);
    y += 13 + split.length * 13 + 6;
  };
  const partHeader = (n, title) => {
    ensureRoom(50);
    y += 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor("#2B5339");
    doc.text(`PART ${n}`, margin, y);
    y += 12;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor("#1C2B23");
    doc.text(title, margin, y);
    y += 8;
    doc.setDrawColor("#2B5339");
    doc.setLineWidth(1.5);
    doc.line(margin, y, margin + 40, y);
    doc.setLineWidth(0.4);
    y += 14;
  };

  // ─── Cover ─────────────────────────────────────────────────
  doc.setFillColor(43, 83, 57);
  doc.rect(0, 0, pageW, 140, "F");
  doc.setTextColor("#F9F8F6");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("MYCENTENNIALPLAN · FUTURE BUILDER WORKBOOK", margin, 50);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(28);
  doc.text("My Retirement", margin, 90);
  doc.text("Readiness Workbook", margin, 118);
  y = 170;

  const totalScore = Object.values(w.scores || {}).reduce((a, v) => a + (Number(v) || 0), 0);
  const tier = TIER(totalScore);

  // ── Meta card ────
  line(w.meta?.name || "Name: ____________________", { size: 14, bold: true, spaceAfter: 2 });
  const metaBits = [
    w.meta?.today && `Today: ${w.meta.today}`,
    w.meta?.target_year && `Target retirement year: ${w.meta.target_year}`,
    w.meta?.target_age && `Target retirement age: ${w.meta.target_age}`,
  ].filter(Boolean);
  if (metaBits.length) line(metaBits.join("   ·   "), { size: 10, color: "#5C6B62" });

  y += 8;
  rule();

  // ── Score summary ────
  line("READINESS SCORE", { size: 9, bold: true, color: "#2B5339", spaceAfter: 2 });
  line(`${totalScore} / 100  ·  ${tier}`, { size: 22, bold: true, spaceAfter: 6 });
  Object.entries(w.scores || {}).forEach(([k, v]) => {
    line(`   ${TB_LABEL[k] || k}: ${v || 0} / 10`, { size: 10, color: "#5C6B62", spaceAfter: 1 });
  });
  y += 8;

  // ─── Part 1 — Vision ───────────────────────────────────────
  partHeader(1, "Picture your retirement");
  kv("Retire at age", w.vision?.retire_age);
  kv("Years from today", w.vision?.years_from_today);
  if (w.vision?.where?.length) kv("Where might I live", w.vision.where.join(", "));
  kv("Notes", w.vision?.where_notes);
  if (w.vision?.activities?.length) kv("What does retirement look like", w.vision.activities.join(", "));
  kv("Other", w.vision?.activities_other);
  const three = (w.vision?.three_things || []).filter(has);
  if (three.length) kv("Three things I want retirement to give me", three.map((t, i) => `${i + 1}. ${t}`).join("\n"));
  kv("Kitchen-table conversation", w.vision?.kitchen_table);

  // ─── Part 2 — Money today ──────────────────────────────────
  partHeader(2, "Know where you stand today");
  const have = sum(w.money?.have);
  const owe = sum(w.money?.owe);
  line(`Money I have: ${usd(have)}   ·   Money I owe: ${usd(owe)}   ·   Net: ${usd(have - owe)}`, { size: 11, bold: true, spaceAfter: 6 });
  kv("Biggest debt concern", w.money?.debt_biggest_concern);
  kv("First action", w.money?.debt_first_action);

  // ─── Part 3 — Monthly ──────────────────────────────────────
  partHeader(3, "My monthly money");
  const inc = sum(w.monthly?.income);
  const exp = sum(w.monthly?.expense);
  line(`Income: ${usd(inc)}   ·   Expenses: ${usd(exp)}   ·   Available: ${usd(inc - exp)}`, { size: 11, bold: true, spaceAfter: 6 });
  kv("What surprises me", w.monthly?.surprising);
  kv("What I could change", w.monthly?.could_change);
  kv("What I could redirect", w.monthly?.could_redirect);

  // ─── Part 4 — Emergency ────────────────────────────────────
  partHeader(4, "My emergency toolbox");
  const em = w.emergency || {};
  kv("$1,000 unexpected expense", em.thousand);
  kv("One month without income", em.one_month);
  kv("Three months", em.three);
  kv("Six months", em.six);
  const emGoal = Number(em.goal) || 0;
  const emHave = Number(em.have) || 0;
  if (emGoal || emHave) line(`Fund today: ${usd(emHave)}  ·  Goal: ${usd(emGoal)}  ·  To build: ${usd(Math.max(0, emGoal - emHave))}  ·  Per payday: ${usd(em.automatic)}`,
    { size: 10, spaceAfter: 6 });

  // ─── Part 5 — Retirement ───────────────────────────────────
  partHeader(5, "My retirement toolbox");
  if (w.retirement?.accounts?.length) kv("Accounts", w.retirement.accounts.join(", "));
  kv("Current balance", w.retirement?.balance && usd(w.retirement.balance));
  kv("Monthly contribution", w.retirement?.monthly_contrib && usd(w.retirement.monthly_contrib));
  kv("Or % of income", w.retirement?.pct_income && `${w.retirement.pct_income}%`);
  kv("Employer match", w.retirement?.employer_match);
  kv("Receiving full match", w.retirement?.full_match);
  kv("To investigate", w.retirement?.investigate);

  // ─── Part 6 — Social Security ──────────────────────────────
  partHeader(6, "Social Security");
  kv("Has SSA account", w.social?.has_account);
  kv("Reviewed earnings", w.social?.reviewed_earnings);
  kv("Knows estimate", w.social?.knows_estimate);
  kv("Early / Full / Age 70",
    [w.social?.est_early && usd(w.social.est_early),
     w.social?.est_full && usd(w.social.est_full),
     w.social?.est_70 && usd(w.social.est_70)].filter(Boolean).join(" / "));
  kv("Planned claiming age", w.social?.planned_age);
  kv("Why", w.social?.why);
  kv("Still need", w.social?.need_info);

  // ─── Part 7 — Healthcare ───────────────────────────────────
  partHeader(7, "Healthcare");
  if (w.healthcare?.pre_medicare_source?.length) kv("Before Medicare coverage", w.healthcare.pre_medicare_source.join(", "));
  if (w.healthcare?.medicare_flags?.length) kv("Medicare readiness", w.healthcare.medicare_flags.join(", "));
  kv("Biggest healthcare question", w.healthcare?.biggest_question);

  // ─── Part 8 — Investing ────────────────────────────────────
  partHeader(8, "Investing & risk");
  if (w.investing?.knows?.length) kv("I know", w.investing.knows.join(", "));
  kv("Reaction to a market drop", w.investing?.reaction);

  // ─── Part 9 — Income ───────────────────────────────────────
  partHeader(9, "Retirement income");
  const retIn = sum(w.income?.sources);
  const retSp = sum(w.income?.spending);
  line(`Monthly income: ${usd(retIn)}   ·   Monthly spending: ${usd(retSp)}   ·   Difference: ${usd(retIn - retSp)}`,
    { size: 11, bold: true, spaceAfter: 6 });
  kv("Plan state", w.income?.plan_state);

  // ─── Part 10 — Taxes ───────────────────────────────────────
  partHeader(10, "Taxes & withdrawals");
  if (w.taxes?.knows?.length) kv("I understand", w.taxes.knows.join(", "));
  kv("Biggest tax question", w.taxes?.biggest_question);

  // ─── Part 11 — Estate ──────────────────────────────────────
  partHeader(11, "Protect what you've built");
  if (w.estate?.checks?.length) kv("Family protection check", w.estate.checks.join(", "));
  kv("Last review", w.estate?.last_review);
  kv("Next review", w.estate?.next_review);

  // ─── Part 12 — Life plan ───────────────────────────────────
  partHeader(12, "Retirement is also a life plan");
  kv("Monday morning I imagine myself", w.life?.monday_morning);
  kv("People", w.life?.people);
  kv("Learn", w.life?.learn);
  kv("Help others", w.life?.help);
  kv("Places", w.life?.places);
  kv("Always wanted to do", w.life?.always_wanted);
  const b = w.life?.bucket || {};
  if (b.do || b.go || b.learn || b.share) {
    kv("Bucket list · Do", b.do);
    kv("Bucket list · Go", b.go);
    kv("Bucket list · Learn", b.learn);
    kv("Bucket list · Share", b.share);
  }

  // ─── Top 5 actions ─────────────────────────────────────────
  partHeader(14, "My top five");
  (w.actions || []).forEach((a, i) => {
    if (!has(a.need_to) && !has(a.first_step) && !has(a.by_when)) return;
    line(`#${i + 1}`, { size: 11, bold: true, spaceAfter: 2 });
    if (has(a.need_to)) line(`I need to: ${a.need_to}`, { size: 10 });
    if (has(a.first_step)) line(`First step: ${a.first_step}`, { size: 10 });
    if (has(a.by_when)) line(`By: ${a.by_when}`, { size: 10, spaceAfter: 6 });
  });

  // ─── One-year promise + Future You ────────────────────────
  y += 8;
  rule();
  if (has(w.one_year_promise)) {
    line("MY ONE-YEAR PROMISE", { size: 9, bold: true, color: "#2B5339" });
    line(w.one_year_promise, { size: 11, italic: true, spaceAfter: 8 });
    if (has(w.signature) || has(w.signature_date)) {
      line(`Signed: ${w.signature || "_______________"}    ·    ${w.signature_date || ""}`,
        { size: 10, color: "#5C6B62", spaceAfter: 8 });
    }
  }
  if (has(w.future_you)) {
    line("DEAR FUTURE ME…", { size: 9, bold: true, color: "#2B5339" });
    line(w.future_you, { size: 11, italic: true, spaceAfter: 8 });
  }

  // ─── Footer ───────────────────────────────────────────────
  rule();
  line("This workbook is for educational purposes only and does not provide individualised financial, investment, tax, legal, Social Security, Medicare, or healthcare advice. Verify current information with the appropriate official source and qualified professionals when appropriate.",
    { size: 8, color: "#5C6B62" });
  line("MyCentennialPlan · Learn · Share · Grow", { size: 9, bold: true, color: "#2B5339" });

  const name = (w.meta?.name || "workbook").replace(/[^a-z0-9]+/gi, "-").toLowerCase().replace(/^-+|-+$/g, "") || "workbook";
  doc.save(`centennial-readiness-workbook-${name}.pdf`);
}
